import React, { useState } from "react";
import { Save, ChevronDown, ChevronRight, Plus, Minus, UserCheck, FileWarning, Coffee, EyeOff, CalendarPlus } from "lucide-react";
import { formatNum } from "@/lib/api";
import LeaveQuickAddModal from "./LeaveQuickAddModal";
import { IadeCell, EntryBadges, EkNote, liveYemek, liveYol } from "./IadeCell";
import { useAuth } from "@/lib/auth";

const MASK = "•••••";
const maskNum = (v, canSee) => (canSee ? formatNum(v) : MASK);

export default function PayrollMobileCards({
  rows,
  expanded,
  onToggle,
  updateLocal,
  saveRow,
  quickBump,
  savingId,
  canSeeMoney,
  canSeeTotals,
  yil,
  ay,
  onReloadRows,
}) {
  const [quickLeave, setQuickLeave] = useState(null); // { personnel, tur }
  return (
    <div className={canSeeMoney ? "md:hidden divide-y divide-slate-100" : "divide-y divide-slate-100"} data-testid="payroll-cards">
      {rows.map((row, idx) => (
        <PayrollMobileCard
          key={row.personnel_id}
          row={row}
          idx={idx}
          expanded={!!expanded[row.personnel_id]}
          onToggle={() => onToggle(row.personnel_id)}
          onChange={(field, value) => updateLocal(idx, field, value)}
          onSave={() => saveRow(row)}
          onQuickBump={(field, delta) => quickBump(idx, field, delta)}
          onQuickLeave={(tur) => setQuickLeave({ personnel: { id: row.personnel_id, ad_soyad: row.ad_soyad }, tur })}
          saving={savingId === row.personnel_id}
          canSeeMoney={canSeeMoney}
        />
      ))}
      {canSeeTotals && rows.length > 0 && (
        <div className="p-4 bg-slate-50 border-t border-slate-200" data-testid="payroll-cards-totals">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Aylık Toplam</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div><span className="text-slate-400">Brüt: </span><span className="font-mono-tab font-semibold">{formatNum(rows.reduce((s, r) => s + (r.brut_maas || 0), 0))}</span></div>
            <div><span className="text-slate-400">Kesinti: </span><span className="font-mono-tab font-semibold text-rose-700">{formatNum(rows.reduce((s, r) => s + r.toplam_kesinti, 0))}</span></div>
            <div><span className="text-slate-400">Prim: </span><span className="font-mono-tab font-semibold text-emerald-700">{formatNum(rows.reduce((s, r) => s + (r.prim_from_records || 0), 0))}</span></div>
            <div><span className="text-slate-400">Yol: </span><span className="font-mono-tab font-semibold">{formatNum(rows.reduce((s, r) => s + liveYol(r), 0))}</span></div>
            <div className="col-span-2 pt-1 border-t border-slate-200">
              <span className="text-slate-400">Toplam Net: </span>
              <span className="font-mono-tab font-bold text-emerald-700 text-sm" data-testid="total-net-mobile">{formatNum(rows.reduce((s, r) => s + r.net_maas, 0))}</span>
            </div>
          </div>
        </div>
      )}
      {quickLeave && (
        <LeaveQuickAddModal
          personnel={quickLeave.personnel}
          tur={quickLeave.tur}
          yil={yil}
          ay={ay}
          onClose={() => setQuickLeave(null)}
          onSaved={() => { setQuickLeave(null); onReloadRows?.(); }}
        />
      )}
    </div>
  );
}

function PayrollMobileCard({ row, idx, expanded, onToggle, onChange, onSave, onQuickBump, onQuickLeave, saving, canSeeMoney = true }) {
  const sicil = row.sicil_no;
  const { isAdmin } = useAuth();
  const upd = (_i, f, v) => onChange(f, v);
  const ym = liveYemek(row);
  return (
    <div data-testid={`payroll-card-${sicil}`} className="p-4">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 text-left"
        data-testid={`payroll-card-toggle-${sicil}`}
      >
        <div className="shrink-0 w-7 h-7 rounded-lg bg-slate-100 inline-flex items-center justify-center text-slate-600">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{row.ad_soyad}</div>
          <div className="text-xs text-slate-500 truncate">{row.pozisyon} · {sicil}</div>
          {row.kesinti_notu && <div className="text-[10px] font-semibold text-sky-700 truncate" data-testid={`m-kesinti-notu-${sicil}`}>{row.kesinti_notu}</div>}
          {(row.cikis_gunu || row.giris_gunu || row.maas_bekleniyor) && <div className="flex flex-wrap gap-1 mt-1"><EntryBadges row={row} tp="m-" /></div>}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Net</div>
          <div className="font-mono-tab font-bold text-emerald-700" data-testid={`net-${sicil}`}>{maskNum(row.net_maas, canSeeMoney)}</div>
        </div>
      </button>

      {/* Hızlı Puantaj — her zaman görünür (collapsed olsa bile) */}
      <div className="mt-3 grid grid-cols-3 gap-2" data-testid={`quick-puantaj-${sicil}`} onClick={(e) => e.stopPropagation()}>
        <QuickStepper
          testid={`qp-ucretli-${sicil}`}
          icon={<UserCheck size={11} />}
          label="Ücretli İzin"
          value={row.ucretli_izin_gun}
          yillikChip={row.ucretli_izin_yillik}
          tone="blue"
          onDec={() => onQuickBump("ucretli_izin_manuel", -0.5)}
          onInc={() => onQuickBump("ucretli_izin_manuel", +0.5)}
          onCal={() => onQuickLeave?.("ucretli_izin")}
          calTestid={`quick-ucretli-${sicil}`}
        />
        <QuickStepper
          testid={`qp-ucretsiz-${sicil}`}
          icon={<Coffee size={11} />}
          label="Ücretsiz"
          value={row.ucretsiz_izin_gun}
          yillikChip={row.ucretsiz_izin_yillik}
          tone="amber"
          onDec={() => onQuickBump("ucretsiz_izin_manuel", -0.5)}
          onInc={() => onQuickBump("ucretsiz_izin_manuel", +0.5)}
          onCal={() => onQuickLeave?.("ucretsiz_izin")}
          calTestid={`quick-ucretsiz-${sicil}`}
        />
        <QuickStepper
          testid={`qp-raporlu-${sicil}`}
          icon={<FileWarning size={11} />}
          label="Raporlu"
          value={row.raporlu_gun}
          yillikChip={row.raporlu_yillik}
          tone="rose"
          onDec={() => onQuickBump("raporlu_manuel", -0.5)}
          onInc={() => onQuickBump("raporlu_manuel", +0.5)}
          onCal={() => onQuickLeave?.("rapor")}
          calTestid={`quick-rapor-${sicil}`}
        />
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          {canSeeMoney && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <SnapRow label="Sabit Maaş (Net)" value={formatNum(row.sabit_net_maas)} />
              <SnapRow label="Banka Listesi" value={formatNum(row.banka_listesi)} />
              <SnapRow label="Prim Ödemesi" value={formatNum(row.prim_odemesi || 0)} />
              <SnapRow label="Brüt (Normal+Mesai)" value={formatNum(row.brut_maas)} />
              <div className="bg-white border border-slate-200 rounded-md px-2 py-1.5" data-testid={`m-yemek-${sicil}`}>
                <div className="text-[10px] text-slate-500">Yemek Toplam*</div>
                <div className="font-mono-tab font-semibold text-indigo-700">{formatNum(ym.toplam)}</div>
                <EkNote row={row} kind="yemek" tp="m-" />
                <IadeCell row={row} idx={idx} kind="yemek" isAdmin={isAdmin} updateLocal={upd} tp="m-" />
                {ym.kesinti > 0 && <div className="text-[10px] font-bold text-rose-700" data-testid={`m-yemek-maas-kesinti-${sicil}`}>maaştan −{formatNum(ym.kesinti)}</div>}
              </div>
              <div className="bg-white border border-slate-200 rounded-md px-2 py-1.5" data-testid={`m-yol-${sicil}`}>
                <div className="text-[10px] text-slate-500">Yol Ücreti</div>
                <div className={`font-mono-tab font-semibold ${liveYol(row) < 0 ? "text-rose-700" : ""}`}>{formatNum(liveYol(row))}</div>
                <EkNote row={row} kind="yol" tp="m-" />
                <IadeCell row={row} idx={idx} kind="yol" isAdmin={isAdmin} updateLocal={upd} tp="m-" />
              </div>
              <SnapRow label="Prim (Oto)" value={formatNum(row.prim_from_records || 0)} tone="emerald" />
              <SnapRow label="Kıdem Primi*" value={formatNum(row.kidem_primi || 0)} tone="emerald" />
              <SnapRow label="Avans Kesinti" value={formatNum(row.avans_kesintisi)} tone="rose" />
              <SnapRow label="Toplam Kesinti" value={formatNum(row.toplam_kesinti)} tone="rose" />
            </div>
          )}
          {!canSeeMoney && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
              <EyeOff size={14} className="mt-0.5 shrink-0" />
              <span><strong>Muhasebeci görünümü:</strong> Maaş/prim rakamları yalnızca yönetici tarafından görüntülenebilir. Puantaj girişleri aşağıda düzenlenebilir.</span>
            </div>
          )}

          <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
            <MField label="Çalışılan Gün">
              <input data-testid={`calisma-gun-${sicil}`} type="number" step="0.5" value={row.calisma_gunu} onChange={(e) => onChange("calisma_gunu", e.target.value)} className="m-input" />
            </MField>
            <div className="grid grid-cols-2 gap-2">
              <MField label={`Ücretli İzin${Number(row.ucretli_izin_yillik)>0 ? ` (+${row.ucretli_izin_yillik} yıllık)` : ""}`}>
                <input type="number" step="0.5" value={row.ucretli_izin_manuel ?? row.ucretli_izin_gun} onChange={(e) => onChange("ucretli_izin_manuel", e.target.value)} className="m-input" />
              </MField>
              <MField label={`Ücretsiz İzin${Number(row.ucretsiz_izin_yillik)>0 ? ` (+${row.ucretsiz_izin_yillik})` : ""}`}>
                <input type="number" step="0.5" value={row.ucretsiz_izin_manuel ?? row.ucretsiz_izin_gun} onChange={(e) => onChange("ucretsiz_izin_manuel", e.target.value)} className="m-input" />
              </MField>
            </div>
            <MField label={`Raporlu Gün${Number(row.raporlu_yillik)>0 ? ` (+${row.raporlu_yillik})` : ""}`}>
              <input type="number" step="0.5" value={row.raporlu_manuel ?? row.raporlu_gun} onChange={(e) => onChange("raporlu_manuel", e.target.value)} className="m-input" />
            </MField>
            {canSeeMoney && (
              <>
                <MField label="Fazla Mesai (₺)">
                  <input data-testid={`mesai-${sicil}`} type="number" step="0.01" value={row.fazla_mesai_tutar} onChange={(e) => onChange("fazla_mesai_tutar", e.target.value)} className="m-input" />
                </MField>
                <div className="grid grid-cols-3 gap-2">
                  <MField label="Yemek/Gün (₺)">
                    <input data-testid={`yemek-gunluk-${sicil}`} type="number" step="0.01" value={row.yemek_gunluk_tutar} onChange={(e) => onChange("yemek_gunluk_tutar", e.target.value)} className="m-input" />
                  </MField>
                  <MField label="Yemek Gün">
                    <input data-testid={`yemek-gun-${sicil}`} type="number" step="0.5" value={row.yemek_gun_sayisi} onChange={(e) => onChange("yemek_gun_sayisi", e.target.value)} className="m-input" />
                  </MField>
                  <MField label="Yemek Ekstra">
                    <input data-testid={`yemek-ekstra-${sicil}`} type="number" step="0.01" value={row.yemek_ekstra || 0} onChange={(e) => onChange("yemek_ekstra", e.target.value)} className="m-input" />
                  </MField>
                </div>
                <MField label="Yol Günlük (₺)">
                  <input data-testid={`yol-gunluk-${sicil}`} type="number" step="0.01" value={row.yol_gunluk_ucret || 0} onChange={(e) => onChange("yol_gunluk_ucret", e.target.value)} className="m-input" />
                </MField>
                <MField label="Manuel Prim (₺)">
                  <input data-testid={`manuel-prim-${sicil}`} type="number" step="0.01" value={row.manuel_prim || 0} onChange={(e) => onChange("manuel_prim", e.target.value)} className="m-input" />
                </MField>
                <MField label="Ek Kesinti (₺)">
                  <input type="number" step="0.01" value={row.ek_kesinti} onChange={(e) => onChange("ek_kesinti", e.target.value)} className="m-input" />
                </MField>
              </>
            )}
            {!canSeeMoney && (
              <MField label="Yemek Gün">
                <input data-testid={`yemek-gun-${sicil}`} type="number" step="0.5" value={row.yemek_gun_sayisi} onChange={(e) => onChange("yemek_gun_sayisi", e.target.value)} className="m-input" />
              </MField>
            )}
          </div>

          <button
            data-testid={`save-row-${sicil}`}
            onClick={onSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50"
          >
            <Save size={14} />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      )}
      <style>{`.m-input { width: 100%; padding: 8px 10px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; background: #fff; font-feature-settings: "tnum"; } .m-input:focus { border-color: #0F172A; box-shadow: 0 0 0 2px rgba(15,23,42,0.1); }`}</style>
    </div>
  );
}

function QuickStepper({ icon, label, value, tone, onDec, onInc, onCal, calTestid, yillikChip, testid }) {
  const toneMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  const btnMap = {
    blue: "bg-blue-600 hover:bg-blue-700",
    amber: "bg-amber-500 hover:bg-amber-600",
    rose: "bg-rose-600 hover:bg-rose-700",
  };
  const yillik = Number(yillikChip || 0);
  return (
    <div data-testid={testid} className={`rounded-lg border p-2 ${toneMap[tone]}`}>
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-80 mb-1">
        {icon} <span className="flex-1">{label}</span>
        {onCal && (
          <button
            type="button"
            onClick={onCal}
            className={`shrink-0 w-5 h-5 rounded text-white inline-flex items-center justify-center ${btnMap[tone]}`}
            title="Tarih aralığı ile ekle"
            data-testid={calTestid}
            aria-label="Tarih aralığı"
          ><CalendarPlus size={11}/></button>
        )}
      </div>
      <div className="flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={onDec}
          disabled={Number(value) <= 0}
          className={`shrink-0 w-7 h-7 rounded-md text-white inline-flex items-center justify-center disabled:opacity-30 ${btnMap[tone]}`}
          data-testid={`${testid}-dec`}
          aria-label="Azalt"
        ><Minus size={12} /></button>
        <div className="font-mono-tab font-bold text-sm text-center flex-1" data-testid={`${testid}-value`}>
          {Number(value || 0).toFixed(1).replace(".", ",")}
        </div>
        <button
          type="button"
          onClick={onInc}
          className={`shrink-0 w-7 h-7 rounded-md text-white inline-flex items-center justify-center ${btnMap[tone]}`}
          data-testid={`${testid}-inc`}
          aria-label="Artır"
        ><Plus size={12} /></button>
      </div>
      {yillik > 0 && (
        <div className="mt-1 text-[9px] font-bold text-center bg-white/60 rounded px-1 py-0.5 border border-current">
          +{yillik} yıllık
        </div>
      )}
    </div>
  );
}

function SnapRow({ label, value, tone }) {
  const toneCls = tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : tone === "indigo" ? "text-indigo-700" : "text-slate-900";
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-slate-100">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span className={`font-mono-tab text-xs font-semibold ${toneCls}`}>{value}</span>
    </div>
  );
}

function MField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
